import type { Editor } from '@tiptap/react';
import type Contact from '../../../models/Contact';
import type { AdminChatAssistResult } from '../../../models/admin/AdminChatAssistResult';
import type { VeraPromptProposal } from '../../../models/admin';

export interface PreviewAssistTranscriptMessage {
  id: string;
  senderRole: 'investor' | 'admin';
  senderName: string;
  text: string;
  timestamp: string;
}

export interface AdminAssistDraft {
  markdown: string;
  intentText?: string;
  groundingText?: string;
  contactId: string | null;
  contactLabel: string | null;
  transcript: PreviewAssistTranscriptMessage[];
  savedAt: string;
}

export const DRAFT_KEY = 'vera_admin_assist_proposal_draft';

export const INTENT_CHIP_COLOR: Record<string, 'warning' | 'info' | 'success'> = {
  ADVICE: 'warning',
  LOCATION: 'info',
  ALLOWED: 'success',
};

export function draftTextEqual(left: string | undefined, right: string): boolean {
  return (left ?? '').trim() === right.trim();
}

export function adminAssistDraftHasUnsavedChanges(
  draft: AdminAssistDraft,
  production: { markdown: string; intent: string; grounding: string },
): boolean {
  const hasTranscript = (draft.transcript?.length ?? 0) > 0;
  return (
    hasTranscript ||
    !draftTextEqual(draft.markdown, production.markdown) ||
    (draft.intentText !== undefined && !draftTextEqual(draft.intentText, production.intent)) ||
    (draft.groundingText !== undefined &&
      !draftTextEqual(draft.groundingText, production.grounding))
  );
}

export function parseAdminAssistDraft(raw: string | null): AdminAssistDraft | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminAssistDraft;
  } catch {
    return null;
  }
}

export function formatChangedPromptLabel(promptKey: string): string {
  if (promptKey === 'admin_assist') return 'Coaching Prompt';
  if (promptKey === 'admin_assist_intent') return 'Classifier Prompt';
  if (promptKey === 'admin_assist_grounding') return 'Google Search Prompt';
  return promptKey;
}

export interface VeraAdminChatAssistPromptSandboxState {
  sessionId: string;
  promptTab: number;
  setPromptTab: (tab: number) => void;
  rawMode: boolean;
  setRawMode: (raw: boolean) => void;
  markdown: string;
  productionMarkdown: string;
  promptSynced: boolean;
  isDirty: boolean;
  intentText: string;
  intentProductionText: string;
  intentSynced: boolean;
  intentIsDirty: boolean;
  groundingText: string;
  groundingProductionText: string;
  groundingSynced: boolean;
  groundingIsDirty: boolean;
  contactSearch: string;
  setContactSearch: (value: string) => void;
  contactOptions: Contact[];
  contactLoading: boolean;
  selectedContact: Contact | null;
  setSelectedContact: (contact: Contact | null) => void;
  transcript: PreviewAssistTranscriptMessage[];
  investorComposeText: string;
  setInvestorComposeText: (value: string) => void;
  adminComposeText: string;
  setAdminComposeText: (value: string) => void;
  assistLoading: boolean;
  assistResult: AdminChatAssistResult | null;
  assistError: string | null;
  saving: boolean;
  error: string | null;
  submittedProposal: VeraPromptProposal | null;
  savedDraft: AdminAssistDraft | null;
  editor: Editor | null;
  hasInvestorMessage: boolean;
  handleRawMarkdownChange: (value: string) => void;
  handleIntentChange: (value: string) => void;
  resetIntentToProduction: () => void;
  handleGroundingChange: (value: string) => void;
  resetGroundingToProduction: () => void;
  dismissDraft: () => void;
  addMessage: (role: 'investor' | 'admin', rawText: string) => void;
  resetSandbox: () => void;
  runAssist: () => Promise<void>;
  submitProposal: () => void;
  restoreDraft: (draft: AdminAssistDraft) => void;
  syncEditorFromMarkdown: () => void;
}
